import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Popover,
  TextField,
  InputAdornment,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import type { DynamicColumn } from '../ui/DynamicTable';

/** Columns that get ⋮ sort/filter (excludes Sync checkbox column). */
export function isCdpResourceFilterSortColumn(columnId: string, columnLabel?: string): boolean {
  if (columnId === 'sync') return false;
  const k = columnId.toLowerCase();
  if (k === 'leadid' || k === 'value' || k === 'codingsystem') return true;
  const codeKeys = new Set([
    'conditionid',
    'procedureid',
    'allergyid',
    'immunizationid',
    'deviceid',
    'id',
    'familymemberid',
  ]);
  if (codeKeys.has(k)) return true;
  const lab = (columnLabel || '').trim().toLowerCase();
  return (
    lab === 'lead id' ||
    lab === 'code' ||
    lab === 'value' ||
    lab === 'coding system' ||
    lab === 'family member id'
  );
}

export function getCdpResourceCellString(row: Record<string, unknown>, columnId: string): string {
  const v = row[columnId];
  if (v == null || v === '') return '';
  return String(v).trim();
}

const NUMERIC_CODE_COLUMN_IDS = new Set([
  'conditionid',
  'procedureid',
  'allergyid',
  'immunizationid',
  'deviceid',
  'id',
  'familymemberid',
]);

export function compareCdpResourceRows(
  a: Record<string, unknown>,
  b: Record<string, unknown>,
  columnId: string,
  order: 'asc' | 'desc'
): number {
  const mult = order === 'asc' ? 1 : -1;
  const k = columnId.toLowerCase();
  if (NUMERIC_CODE_COLUMN_IDS.has(k)) {
    const na = Number(a[columnId]);
    const nb = Number(b[columnId]);
    if (Number.isFinite(na) && Number.isFinite(nb)) {
      return mult * (na - nb);
    }
  }
  const sa = getCdpResourceCellString(a, columnId).toLowerCase();
  const sb = getCdpResourceCellString(b, columnId).toLowerCase();
  return mult * sa.localeCompare(sb, undefined, { numeric: true, sensitivity: 'base' });
}

interface CdpResourceColumnHeaderProps {
  label: string;
  filterText: string;
  onFilterTextChange: (value: string) => void;
  activeSort: 'asc' | 'desc' | null;
  onSortAsc: () => void;
  onSortDesc: () => void;
}

const CdpResourceColumnHeader: React.FC<CdpResourceColumnHeaderProps> = ({
  label,
  filterText,
  onFilterTextChange,
  activeSort,
  onSortAsc,
  onSortDesc,
}) => {
  const iconRef = useRef<HTMLButtonElement>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [filterOpen, setFilterOpen] = useState(false);

  const openMenu = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  };

  const closeMenu = () => setMenuAnchor(null);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 0.25,
        width: '100%',
        minWidth: 0,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <Typography
        component="span"
        sx={{ fontSize: 'inherit', fontWeight: 600, flex: '1 1 auto', minWidth: 0 }}
        noWrap
        title={label}
      >
        {label}
      </Typography>
      <IconButton
        ref={iconRef}
        size="small"
        aria-label={`${label} column options`}
        sx={{ p: 0.25, flexShrink: 0 }}
        onClick={openMenu}
      >
        <MoreVertIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            onSortDesc();
            closeMenu();
          }}
          selected={activeSort === 'desc'}
          dense
        >
          Sort by descending
        </MenuItem>
        <MenuItem
          onClick={() => {
            onSortAsc();
            closeMenu();
          }}
          selected={activeSort === 'asc'}
          dense
        >
          Sort by ascending
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeMenu();
            setFilterOpen(true);
          }}
          dense
        >
          Filter by values
        </MenuItem>
      </Menu>
      <Popover
        open={filterOpen}
        anchorEl={iconRef.current}
        onClose={() => setFilterOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 1.5, minWidth: 220 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search in column…"
            value={filterText}
            onChange={(e) => onFilterTextChange(e.target.value)}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            variant="outlined"
          />
        </Box>
      </Popover>
    </Box>
  );
};

export function CdpResourceTableResetBar(props: { hasActive: boolean; onReset: () => void }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        px: 1,
        pt: 1,
        pb: 0.5,
      }}
    >
      <Button
        size="small"
        variant="text"
        disabled={!props.hasActive}
        onClick={props.onReset}
        sx={{
          fontSize: '12px',
          fontWeight: 600,
          textTransform: 'none',
          color: (theme) => theme.palette.themeColor?.main || theme.palette.primary.main,
        }}
      >
        Reset filters
      </Button>
    </Box>
  );
}

export function useCdpResourceTable<T extends Record<string, unknown>>(
  baseColumns: DynamicColumn<T>[],
  rawData: T[] | undefined
): {
  columns: DynamicColumn<T>[];
  tableData: T[];
  hasActiveFilterOrSort: boolean;
  onResetFilters: () => void;
} {
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [sortState, setSortState] = useState<{ columnId: string; order: 'asc' | 'desc' } | null>(
    null
  );

  const tableData = useMemo(() => {
    const labelById: Record<string, string | undefined> = Object.fromEntries(
      baseColumns.map((c) => [c.id, c.label])
    );
    let rows: T[] = [...(rawData || [])];

    const activeFilters = Object.entries(columnFilters).filter(([, v]) => v?.trim());
    for (const [colId, raw] of activeFilters) {
      if (!isCdpResourceFilterSortColumn(colId, labelById[colId])) continue;
      const needle = raw.trim().toLowerCase();
      rows = rows.filter((row) =>
        getCdpResourceCellString(row as Record<string, unknown>, colId)
          .toLowerCase()
          .includes(needle)
      );
    }

    if (
      sortState &&
      isCdpResourceFilterSortColumn(
        sortState.columnId,
        baseColumns.find((c) => c.id === sortState.columnId)?.label
      )
    ) {
      rows = [...rows].sort((a, b) =>
        compareCdpResourceRows(
          a as Record<string, unknown>,
          b as Record<string, unknown>,
          sortState.columnId,
          sortState.order
        )
      );
    }

    return rows;
  }, [rawData, columnFilters, sortState, baseColumns]);

  const columns = useMemo(() => {
    return baseColumns.map((col) => {
      if (!isCdpResourceFilterSortColumn(col.id, col.label)) {
        return col;
      }
      return {
        ...col,
        renderHeader: () => (
          <CdpResourceColumnHeader
            label={col.label}
            filterText={columnFilters[col.id] ?? ''}
            onFilterTextChange={(value) =>
              setColumnFilters((prev) => {
                const next = { ...prev, [col.id]: value };
                if (!value.trim()) {
                  delete next[col.id];
                }
                return next;
              })
            }
            activeSort={sortState?.columnId === col.id ? sortState.order : null}
            onSortAsc={() => setSortState({ columnId: col.id, order: 'asc' })}
            onSortDesc={() => setSortState({ columnId: col.id, order: 'desc' })}
          />
        ),
      };
    });
  }, [baseColumns, columnFilters, sortState]);

  const hasActiveColumnFilters = useMemo(
    () => Object.values(columnFilters).some((v) => Boolean(v?.trim())),
    [columnFilters]
  );
  const hasActiveFilterOrSort = hasActiveColumnFilters || sortState !== null;

  const onResetFilters = useCallback(() => {
    setColumnFilters({});
    setSortState(null);
  }, []);

  return { columns, tableData, hasActiveFilterOrSort, onResetFilters };
}
